import React, { useState, useMemo } from 'react';
import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import {
  Button,
  Box,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Modal,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@material-ui/core';

import { Employee_Salary, Employee_Expense } from 'variables/expenseTypes';

const EmployeeInfoModal = (props) => {

  const [tabValue, setTabValue] = useState(0);

  const expectedList = props.employeeInfo.expectedSalaries.map(expectedSalary => ({
    expected_salary: expectedSalary.total,
    month_year: moment(expectedSalary.monthYear).format("MMMM / YYYY"),
    crt_date: moment(expectedSalary.createdAt).format("DD/MM/YYYY")
  }));

  const { totalBalance, salaryList, expenseList, totalExpensesList } = useMemo(() => {

    let totalBalanceBuffer = 0;
    let salaryListBuffer = [];
    let expenseListBuffer = [];
    let totalExpensesListBuffer = [];

    let startDate = moment(props.employeeInfo.joiningDate);
    let todaysDate = moment().startOf('day');

    for ( let i = 0; i < props.expenses.length; i++ ) {
      //Condition to check if any Employee Salary is present in Expenses.
      if(props.employeeInfo.id === props.expenses[i].employeeId) {
        if(props.expenses[i].type === Employee_Salary) {
          const salary = props.expenses[i];
          salaryListBuffer.push({
            displayId: salary.displayId,
            month_year: moment(salary.monthYear).format("MMMM / YYYY"),
            amount: salary.total,
            description: salary.description,
            moment_date: salary.monthYear,
            txn_date: moment(salary.createdAt).format("DD/MM/YYYY")
          });
        }
        else if(props.expenses[i].type === Employee_Expense) {
          const expense = props.expenses[i];
          expenseListBuffer.push({
            displayId: expense.displayId,
            amount: expense.total,
            description: expense.description,
            moment_date: expense.createdAt,
            txn_date: moment(expense.createdAt).format("DD/MM/YYYY")
          });
        }
      }
    }

    //The additional 'OR' condition is important to make sure the loop runs even if the months are the same.
    while (startDate.isBefore(todaysDate) || startDate.isSame(todaysDate, 'month')) {

      let totalMonthExpenses = expenseListBuffer.reduce((prevExpense, currentExpense) => {
        if(startDate.isSame(currentExpense.moment_date, 'month')) return prevExpense + currentExpense.amount;
        else return prevExpense;
      }, 0);
      let totalMonthSalary = salaryListBuffer.find(salary => startDate.isSame(salary.moment_date, 'month'))?.amount || 0;
      let totalMonthExpectedSalary = props.employeeInfo.expectedSalaries.find(expectedSalary => startDate.isSame(expectedSalary.monthYear, 'month'))?.total || 0;

      totalBalanceBuffer += totalMonthExpectedSalary - ( totalMonthExpenses + totalMonthSalary );
      totalExpensesListBuffer.push({
        month_year: startDate.format("MMMM / YYYY"),
        expected_salary: totalMonthExpectedSalary,
        total_expenses: totalMonthExpenses + totalMonthSalary,
        balance: totalMonthExpectedSalary - ( totalMonthExpenses + totalMonthSalary )
      });

      startDate.add(1, 'month');
    }

    return {
      totalBalance: totalBalanceBuffer,
      salaryList: salaryListBuffer,
      expenseList: expenseListBuffer,
      totalExpensesList: totalExpensesListBuffer
    }
    
  }, [props.expenses, props.employeeInfo]);

  const columns = [
    { label: "Expense ID", name: "displayId" },
    { label: "Month & Year", name: "month_year" },
    { label: "Amount", name: "amount" },
    { label: "Description", name: "description" },
    { label: "Expected Salary", name: "expected_salary" },
    { label: "Transaction Date", name: "txn_date" },
    { label: "Creation Date", name: "crt_date" },
    { label: "Total Expenses", name: "total_expenses" },
    {
      label: "Balance Amount",
      name: "balance",
      options: {
        filter: true,
        sort: true,
        viewColumns: false,
        customBodyRender: (value, tableMeta) => (
          <font color={ value < 0 ? "red" : null }>{value}</font>
        )
      }
    }
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const options = {
    selectableRows: 'none',
    viewColumns: false
  };

  return (
    <Modal open={props.open} >
      <Card>
        <CardHeader
          subheader="Review all the Expenses of the Employee"
          title="Employee History"
        />
        <Divider />
        <CardContent>
          <Box>
            <Box
              sx={{
                display: 'flex'
              }}
            >
              <Paper>
                <Tabs
                  value={tabValue}
                  indicatorColor="primary"
                  textColor="primary"
                  onChange={handleTabChange}
                  centered
                >
                  <Tab label="Salary History"/>
                  <Tab label="Employee Expenses"/>
                  <Tab label="Expected Salary"/>
                  <Tab label="Total Expenses"/>
                </Tabs>
              </Paper>
            </Box>
            <Box
              sx={{
                overflow: 'auto',
                height: 458,
              }}
            >
              { tabValue === 0 ?
                <MUIDataTable
                  title={"Salary History"}
                  data={salaryList}
                  columns={columns.filter(column => !["expected_salary","crt_date","total_expenses","balance"].includes(column.name))}
                  options={options}
                />
              : null }
              { tabValue === 1 ?
                <MUIDataTable
                  title={"Employee Expenses"}
                  data={expenseList}
                  columns={columns.filter(column => ["displayId","amount","description","txn_date"].includes(column.name))}
                  options={options}
                />
              : null }
              { tabValue === 2 ?
                <MUIDataTable
                  title={"Expected Salary"}
                  data={expectedList}
                  columns={columns.filter(column => ["month_year","expected_salary","crt_date"].includes(column.name))}
                  options={options}
                />
              : null }
              { tabValue === 3 ?
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginBottom: '1%'
                    }}
                  >
                    <Typography
                      align="center"
                      color="textPrimary"
                      variant="h5"
                    >
                      Total Balance Amount : <font color={ totalBalance < 0 ? "red" : null }>{totalBalance} AED</font>
                    </Typography>
                  </Box>
                  <MUIDataTable
                    title={"Total Expenses"}
                    data={totalExpensesList}
                    columns={columns.filter(column => ["month_year","expected_salary","total_expenses","balance"].includes(column.name))}
                    options={options}
                  />
                </Box>
              : null }
            </Box>
            <Divider />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '1%'
              }}
            >
              <Button
                color="primary"
                variant="contained"
                onClick={props.closeModal}
              >
                Close
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Modal>
  )
};

export default EmployeeInfoModal;